<div
  data-test="position-details-page"
  class="h-full flex flex-col"
  [class]="floatingPageClasses()">
  @if (isLoading()) {
    <lib-overlay-loader />
  }

  @if (positionDetails(); as details) {
    <!-- Floating: header and position info share one card; legacy: no wrapper box -->
    <div
      data-test="position-details-card"
      [class]="
        floating() ? 'floating-card p-2xl flex flex-col flex-1' : 'contents'
      ">
      <!-- Header -->
      <header
        [class.border-b]="!floating()"
        [class.border-neutral-300]="!floating()"
        [class.py-lg]="!floating()">
        <ng-template #backButton>
          <a
            data-test="position-details-back"
            (click)="goBack()"
            class="bg-white">
            <lib-chevron-down-icon
              [iconClass]="'size-10 text-neutral-900 rotate-90 p-sm rounded-lg cursor-pointer border border-neutral-200'" />
          </a>
        </ng-template>

        @if (!floating()) {
          <div class="flex justify-between items-center mb-2xl">
            <ng-container [ngTemplateOutlet]="backButton" />
          </div>
        }

        <div class="flex justify-between items-center">
          <div class="flex gap-lg items-center flex-wrap">
            @if (floating()) {
              <ng-container [ngTemplateOutlet]="backButton" />
            }
            <div class="flex flex-col gap-md">
              <div class="flex items-center gap-sm flex-wrap">
                <h2 class="font-semibold text-2xl text-neutral-900">
                  {{ details.name }}
                </h2>

                @if (isPositionStatuesEnhancementEnabled()) {
                  <span class="text-neutral-300 font-semibold no-arabic-font"
                    >|</span
                  >
                  <lib-badge
                    [label]="details.state?.label"
                    [badgeClass]="
                      'px-md py-x-sm border-none text-sm font-medium text-white ' +
                      (details.state?.value | userStatus)
                    " />

                  @if (details.state?.value === 'active') {
                    @let filledStatusLabel =
                      details.incumbent_user_id
                        ? 'positions_list_screen_filled_status_filled'
                        : 'positions_list_screen_filled_status_vacant';
                    @let filledStatusClass =
                      details.incumbent_user_id ? 'active' : 'inactive';
                    <lib-badge
                      [label]="filledStatusLabel | transloco"
                      [badgeClass]="
                        'px-md py-x-sm border-none text-sm font-medium text-white ' +
                        (filledStatusClass | userStatus)
                      " />
                  }
                }
              </div>

              <div class="flex items-center gap-sm">
                @if (details.identifier) {
                  <span class="text-primary-500 font-semibold text-base">
                    {{ details.identifier }}
                  </span>
                  <span class="text-neutral-300 font-semibold no-arabic-font"
                    >|</span
                  >
                }
                <p class="text-sm font-medium text-neutral-500">
                  #{{ details.id }}
                </p>
              </div>
            </div>
          </div>

          <a
            [routerLink]="[
              ADMIN_ROUTES.POSITIONS.FORM | tenantPath: 'admin',
              positionId,
            ]"
            id="positions_details_screen_edit_button"
            class="bg-white text-primary-600 font-medium text-base py-sm px-2xl border-none rounded-md gap-sm flex cursor-pointer">
            <lib-edit-icon [iconClass]="'size-5 text-primary-600'" />
            {{ 'positions_details_screen_edit_button' | transloco }}
          </a>
        </div>
      </header>

      <!-- Position Info Section -->
      <div
        class="flex-1"
        [class.py-2xl]="!floating()"
        [class.pt-2xl]="floating()">
        <h5
          class="text-neutral-900 font-semibold text-lg py-lg border-b border-neutral-200 mb-2xl">
          {{ 'positions_details_screen_position_info_section' | transloco }}
        </h5>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-2xl">
          <!-- Position Name -->
          <div class="flex flex-col gap-xs">
            <span class="text-xs2 font-medium text-neutral-500">
              {{ 'positions_details_screen_position_name_label' | transloco }}
            </span>
            <span class="text-sm font-medium text-neutral-900">
              {{
                details.name ||
                  ('positions_details_screen_na_value' | transloco)
              }}
            </span>
          </div>

          <!-- Position ID -->
          <div class="flex flex-col gap-xs">
            <span class="text-xs2 font-medium text-neutral-500">
              {{ 'positions_details_screen_position_id_label' | transloco }}
            </span>
            <span class="text-sm font-medium text-neutral-900">
              {{
                details.identifier ||
                  ('positions_details_screen_na_value' | transloco)
              }}
            </span>
          </div>

          <!-- Legal Entity -->
          <div class="flex flex-col gap-xs">
            <span class="text-xs2 font-medium text-neutral-500">
              {{ 'positions_details_screen_legal_entity_label' | transloco }}
            </span>
            <span class="text-sm font-medium text-neutral-900">
              {{
                details.legal_entity?.name ||
                  ('positions_details_screen_na_value' | transloco)
              }}
            </span>
          </div>

          <!-- Department -->
          <div class="flex flex-col gap-xs">
            <span class="text-xs2 font-medium text-neutral-500">
              {{ 'positions_details_screen_department_label' | transloco }}
            </span>
            <span class="text-sm font-medium text-neutral-900">
              {{
                details.department?.name ||
                  ('positions_details_screen_na_value' | transloco)
              }}
            </span>
          </div>

          <!-- Job Title -->
          <div class="flex flex-col gap-xs">
            <span class="text-xs2 font-medium text-neutral-500">
              {{ 'positions_details_screen_job_title_label' | transloco }}
            </span>
            <span class="text-sm font-medium text-neutral-900">
              {{
                details.job_title?.name ||
                  ('positions_details_screen_na_value' | transloco)
              }}
            </span>
          </div>

          <!-- Radford Grade -->
          <div class="flex flex-col gap-xs">
            <span class="text-xs2 font-medium text-neutral-500">
              {{ 'positions_details_screen_radford_grade_label' | transloco }}
            </span>
            <span class="text-sm font-medium text-neutral-900">
              {{
                details.job_title?.radford_grade?.label ||
                  details.radford_grade?.label ||
                  ('positions_details_screen_na_value' | transloco)
              }}
            </span>
          </div>

          <!-- Career Track -->
          <div class="flex flex-col gap-xs">
            <span class="text-xs2 font-medium text-neutral-500">
              {{ 'positions_details_screen_career_track_label' | transloco }}
            </span>
            <span class="text-sm font-medium text-neutral-900">
              {{
                details.job_title?.track?.label ||
                  details.job_title?.career_track?.label ||
                  details.career_track?.label ||
                  ('positions_details_screen_na_value' | transloco)
              }}
            </span>
          </div>

          <!-- Reports To Position -->
          <div class="flex flex-col gap-xs">
            <span class="text-xs2 font-medium text-neutral-500">
              {{ 'positions_details_screen_reports_to_label' | transloco }}
            </span>
            <span class="text-sm font-medium text-neutral-900">
              {{
                details.reports_to_position?.name ||
                  ('positions_details_screen_na_value' | transloco)
              }}
            </span>
          </div>

          <!-- FTE -->
          <div class="flex flex-col gap-xs">
            <span class="text-xs2 font-medium text-neutral-500">
              {{ 'positions_details_screen_fte_label' | transloco }}
            </span>
            <span class="text-sm font-medium text-neutral-900">
              {{
                details.fte ?? ('positions_details_screen_na_value' | transloco)
              }}
            </span>
          </div>

          <!-- Incumbent -->
          <div class="flex flex-col gap-xs">
            <span class="text-xs2 font-medium text-neutral-500">
              {{ 'positions_details_screen_incumbent_label' | transloco }}
            </span>
            <span class="text-sm font-medium text-neutral-900">
              {{
                details.incumbent?.name ||
                  ('positions_details_screen_na_value' | transloco)
              }}
            </span>
          </div>

          <!-- Effective From -->
          <div class="flex flex-col gap-xs">
            <span class="text-xs2 font-medium text-neutral-500">
              {{ 'positions_details_screen_effective_from_label' | transloco }}
            </span>
            <span class="text-sm font-medium text-neutral-900">
              {{
                details.effective_from ||
                  ('positions_details_screen_na_value' | transloco)
              }}
            </span>
          </div>

          <!-- Effective To -->
          <div class="flex flex-col gap-xs">
            <span class="text-xs2 font-medium text-neutral-500">
              {{ 'positions_details_screen_effective_to_label' | transloco }}
            </span>
            <span class="text-sm font-medium text-neutral-900">
              {{
                details.effective_to ||
                  ('positions_details_screen_na_value' | transloco)
              }}
            </span>
          </div>

          <!-- Status -->
          <div class="flex flex-col gap-xs">
            <span class="text-xs2 font-medium text-neutral-500">
              {{ 'positions_details_screen_status_label' | transloco }}
            </span>
            <span class="text-sm font-medium text-neutral-900">
              {{
                details.position_status?.label ||
                  ('positions_details_screen_na_value' | transloco)
              }}
            </span>
          </div>
        </div>
      </div>
    </div>
  }
</div>
